/**
 * OS Permissions — Phase 1 pure resolver.
 * Mirrors the non-admin portion of SQL `public.resolve_os_permission(_user_id, _os_id)`.
 * Kept dependency-free so it can be unit-tested and reused server-side.
 *
 * NOTE: Platform Admin override is intentionally NOT modeled here. Admin status
 * is a server-side concept resolved via `public.is_admin(_user_id)` inside the
 * SQL resolver. The frontend obtains admin-aware effective permission through
 * `resolveOsPermissionRemote` (RPC to `resolve_os_permission`). Admin is a
 * platform-level override, not true OS ownership, and admins must NOT be
 * stored in `os_user_permissions`.
 */

export type OsAccessLevel = "none" | "reader" | "editor" | "manager";
export type OsPublicAccessLevel = "none" | "reader" | "editor";
export type OsDirectGrantLevel = "reader" | "editor" | "manager";

export interface OsInstanceLike {
  id: string;
  /** Owner. In this project the owner column is `user_id` on operating_system_apps. */
  owner_user_id: string;
  public_access_level: OsPublicAccessLevel;
}

export interface OsDirectGrantLike {
  os_id: string;
  user_id: string;
  access_level: OsDirectGrantLevel;
}

/**
 * Order: owner → direct user grant → public grant → none.
 * Public grants apply only to authenticated users (non-null userId).
 * Public `manager` is never honored.
 */
export function resolveOsPermission(
  userId: string | null | undefined,
  os: OsInstanceLike | null | undefined,
  directGrants: OsDirectGrantLike[] = [],
): OsAccessLevel {
  if (!os) return "none";

  if (userId && userId === os.owner_user_id) return "manager";

  if (userId) {
    const grant = directGrants.find((g) => g.os_id === os.id && g.user_id === userId);
    if (grant && (grant.access_level === "manager" || grant.access_level === "editor" || grant.access_level === "reader")) {
      return grant.access_level;
    }
  }

  if (userId && (os.public_access_level === "reader" || os.public_access_level === "editor")) {
    return os.public_access_level;
  }

  return "none";
}

export function canRead(level: OsAccessLevel): boolean {
  return level !== "none";
}
export function canChangeData(level: OsAccessLevel): boolean {
  return level === "editor" || level === "manager";
}
export function canAlterSystem(level: OsAccessLevel): boolean {
  return level === "manager";
}
export function canManageSharing(level: OsAccessLevel): boolean {
  return level === "manager";
}

// Phase 5 UX helpers — thin, purpose-named wrappers used by frontend to
// decide whether to render a control. Backend still enforces authorization.
export function canReadOs(level: OsAccessLevel): boolean {
  return canRead(level);
}
export function canEditOsData(level: OsAccessLevel): boolean {
  return canChangeData(level);
}
export function canAlterOsSystem(level: OsAccessLevel): boolean {
  return canAlterSystem(level);
}
export function canManageOsSharing(level: OsAccessLevel): boolean {
  return canManageSharing(level);
}
